import { useEffect, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@mui/material';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import { observer } from 'mobx-react-lite';
import { useLocation } from 'react-router-dom';
import { useAuthMenu } from '@/services/authService/authMenu';
import { collectContainerMemoryWarnings } from '@/services/manageService/containerMonitorService';
import { isSessionExpiredPending } from '@/services/sessionExpired';
import userStore from '@/store/admin/userStore';
import {
    canAccessServiceMonitor,
    clearContainerMemoryWarnPending,
    isContainerMemoryWarnPending,
    isSuperadminAccount,
    tryStartContainerMemoryWarnCheck,
} from '@/utils/serviceMonitorAccess';

/**
 * After password login, warn eligible admins when any container memory is above 75%.
 */
const ContainerMemoryWarningDialog = observer(() => {
    const location = useLocation();
    const { data: menuTree = [], isLoading: menuLoading } = useAuthMenu();
    const user = userStore.user;
    const [open, setOpen] = useState(false);
    const [lines, setLines] = useState<string[]>([]);

    useEffect(() => {
        if (location.pathname === '/login') return;
        if (isSessionExpiredPending()) return;
        if (!user) return;
        if (!isContainerMemoryWarnPending()) return;
        const namesReady = (user.roleNames?.length ?? 0) > 0 || isSuperadminAccount(user);
        if (!namesReady && menuLoading) return;

        let cancelled = false;
        if (!canAccessServiceMonitor(user, menuTree)) {
            clearContainerMemoryWarnPending();
            return;
        }
        if (!tryStartContainerMemoryWarnCheck()) return;

        // Defer actuator fan-out so login/menu HTTP slots stay free on UAT HTTP/1.1
        const timer = window.setTimeout(() => {
            collectContainerMemoryWarnings()
                .then((warnings) => {
                    if (cancelled || warnings.length === 0) return;
                    setLines(
                        warnings.map(
                            (item) =>
                                `${item.serviceName} container memory usage exceeds ${item.percent}%.`,
                        ),
                    );
                    setOpen(true);
                })
                .catch(() => {
                    // silent: failed stats are not over-threshold
                });
        }, 2000);
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [location.pathname, user, menuTree, menuLoading]);

    const onClose = () => setOpen(false);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            role="alertdialog"
            aria-labelledby="container-memory-warning-title"
        >
            <DialogTitle id="container-memory-warning-title" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningAmberOutlined color="warning" />
                Container memory warning
            </DialogTitle>
            <DialogContent>
                {lines.map((line) => (
                    <DialogContentText key={line} sx={{ mb: 1 }}>
                        {line}
                    </DialogContentText>
                ))}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="contained" color="warning" autoFocus>
                    OK
                </Button>
            </DialogActions>
        </Dialog>
    );
});

export default ContainerMemoryWarningDialog;
